/**
 * Owner: Masa Bayu
 * File: qxeditor-document-store.ts
 * Date:Time: 2026-10-24 18:20
 * Description: C1-1 — SoT dokumen QXW editor di luar komponen; idiom store sama
 *   qxeditor-hub-auth.ts (state module + listeners + useSyncExternalStore).
 */

import { useSyncExternalStore } from 'react';
import { createQXWDocument, qxwHasFixedPages, type QXWDocument } from '@qx/qxw-core';
import type { ReconstructionReport } from '../../../../packages/qx-editor/src/types';

/** Salinan import yang sedang aktif dalam shell editor. */
export type QxeditorLoadedCopy = {
  readonly document: QXWDocument;
  readonly initialPageId: string | null;
  readonly sourceLabel: string | null;
  /** Kept for Accept Law Face · JPEG /SMask recover (same as local P02 ladder). */
  readonly pdfBytes: Uint8Array | null;
};

export type QxeditorDocumentSnapshot = {
  readonly loaded: QxeditorLoadedCopy;
  readonly reconstruction: ReconstructionReport | null;
};

function emptyLoaded(): QxeditorLoadedCopy {
  return {
    document: createQXWDocument({ title: '' }),
    initialPageId: null,
    sourceLabel: null,
    pdfBytes: null,
  };
}

/** Snapshot kosong — dipakai semasa boot dan selepas projek dipadam. */
export function emptyQxeditorDocumentSnapshot(): QxeditorDocumentSnapshot {
  return { loaded: emptyLoaded(), reconstruction: null };
}

/** Rujukan stabil untuk boot — meniru `const EMPTY` lama supaya identity page key kekal. */
const INITIAL_SNAPSHOT: QxeditorDocumentSnapshot = emptyQxeditorDocumentSnapshot();

export function initialQxeditorDocumentSnapshot(): QxeditorDocumentSnapshot {
  return INITIAL_SNAPSHOT;
}

let snapshot: QxeditorDocumentSnapshot = INITIAL_SNAPSHOT;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function getQxeditorDocumentSnapshot(): QxeditorDocumentSnapshot {
  return snapshot;
}

export function setQxeditorDocumentSnapshot(next: QxeditorDocumentSnapshot): void {
  if (next === snapshot) return;
  snapshot = next;
  emit();
}

export function subscribeQxeditorDocument(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Bacaan reaktif shell; tulis melalui `setQxeditorDocumentSnapshot`. */
export function useQxeditorDocument(): QxeditorDocumentSnapshot {
  return useSyncExternalStore(subscribeQxeditorDocument, getQxeditorDocumentSnapshot);
}

/** D1 — mod Studio dibaca dari predikat SSOT `@qx/qxw-core`, bukan flag tempatan. */
export function qxeditorDocumentHasFixedPages(): boolean {
  return qxwHasFixedPages(snapshot.loaded.document);
}

/** true = mod Halaman (layout tetap) · false = mod Tulis (manual). */
export function useQxeditorIsPageMode(): boolean {
  return useSyncExternalStore(subscribeQxeditorDocument, qxeditorDocumentHasFixedPages);
}
